import type { IActionParams } from '@/store/modules/index';

const TICK_MS = 250;

interface Getters {
  formattedTime: string;
  isRunning: boolean;
}

type ActionParams = IActionParams<ITimerState, Getters>;

interface ITimerState {
  duration: number;
  timeLeft: number;
  running: boolean;
  endsAt: number | null;
  _intervalId: number | null;
}

const initialState = (): ITimerState => ({
  duration: 120,
  timeLeft: 120,
  running: false,
  endsAt: null,
  _intervalId: null,
});

export default {
  namespaced: true,

  state: initialState(),

  mutations: {
    SET_DURATION(state, s) {
      state.duration = s;
    },
    SET_TIME_LEFT(state, s) {
      state.timeLeft = Math.max(0, s | 0);
    },
    SET_RUNNING(state, v) {
      state.running = v;
    },
    SET_ENDS_AT(state, ms) {
      state.endsAt = ms;
    },
    SET_INTERVAL_ID(state, id) {
      state._intervalId = id;
    },
    CLEAR_INTERVAL(state) {
      if (state._intervalId) {
        clearInterval(state._intervalId);
        state._intervalId = null;
      }
    },
    RESET_STATE(state: ITimerState): void {
      Object.assign(state, initialState());
    },
  },

  actions: {
    start({ commit, dispatch }, duration) {
      const endsAt = Date.now() + duration * 1000;
      commit('SET_DURATION', duration);
      commit('SET_TIME_LEFT', duration);
      commit('SET_ENDS_AT', endsAt);
      commit('SET_RUNNING', true);
      const id = setInterval(() => dispatch('tick'), TICK_MS);
      commit('SET_INTERVAL_ID', id);
    },

    pause({ state, commit, dispatch }) {
      if (!state.running) {
        return;
      }
      dispatch('tick');
      commit('SET_RUNNING', false);
      commit('CLEAR_INTERVAL');
      commit('SET_ENDS_AT', null);
    },

    reset({ commit, state, dispatch }, newDuration = state.duration) {
      dispatch('pause');
      commit('SET_DURATION', newDuration);
      commit('SET_TIME_LEFT', newDuration);
    },

    tick({ state, commit, dispatch }) {
      if (!state.running || !state.endsAt) {
        return;
      }
      const remainingMs = state.endsAt - Date.now();
      const seconds = Math.ceil(remainingMs / 1000);
      if (seconds <= 0) {
        commit('SET_TIME_LEFT', 0);
        dispatch('stop');
        return;
      }
      commit('SET_TIME_LEFT', seconds);
    },

    stop({ commit }) {
      commit('CLEAR_INTERVAL');
      commit('SET_RUNNING', false);
      commit('SET_ENDS_AT', null);
    },

    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },

    bootstrap({ state, commit, dispatch }) {
      if (typeof state.endsAt === 'number') {
        const remMs = state.endsAt - Date.now();
        commit('SET_TIME_LEFT', Math.max(0, Math.ceil(remMs / 1000)));
      }
      if (state.running && state.timeLeft > 0) {
        commit('CLEAR_INTERVAL');
        const id = setInterval(() => dispatch('tick'), TICK_MS);
        commit('SET_INTERVAL_ID', id);
      } else {
        commit('SET_RUNNING', false);
        commit('CLEAR_INTERVAL');
        if (typeof state.endsAt === 'number' && state.timeLeft === 0) {
          commit('SET_ENDS_AT', null);
        }
      }
    },
  },

  getters: {
    formattedTime: state => {
      const minutes = Math.floor(state.timeLeft / 60);
      const seconds = state.timeLeft % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    isRunning: state => {
      return state.running;
    },
  },
};
